import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import { Editor as TipTapEditor } from "@tiptap/react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { useContext, useEffect, useRef, useState } from "react";
import { PreviewMessage } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import TemplateEditButton from "components/TemplateEditButton";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import PersonalizeDialog from "features/Influencer/QuickSendIndividualDrawer/Dialogs/PersonalizeDialog";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useRegisterSaveAsNew } from "utils/useRegisterSaveAsNew";

import styles from "./styles";

interface SelectTemplateProps {
  preview: PreviewMessage;
  index: number;
  editor?: TipTapEditor | null;
}

export default function SelectTemplate({
  preview,
  index,
  editor,
}: SelectTemplateProps) {
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);

  const isFollowUpEmail = index > 0;
  const { emailSettings, currentOrg } = useContext(OrganizationUserContext);
  const {
    setTemplateForQuickSend,
    setNewlyAddedTemplateIndex,
    handleRefetchPreviews,
    bentoBrand,
  } = useContext(QuickSendContext);
  const showPersonalize = !!bentoBrand && !!editor && index === 0;
  const [scheduledTemplate, setScheduledTemplate] = useState<Template | null>(
    null,
  );
  const { templates, setCurrentTemplateContentType } = useContext(
    OutreachTemplatesContext,
  );
  const { quickSendBrandId, previewLoading } = useContext(
    QuickSendDrawerContext,
  );
  const { outreachTemplates, followupTemplates, setCreateTemplateDialogOpen } =
    useContext(OutreachTemplatesContext);

  const handleRefetchPreviewsRef = useRef(handleRefetchPreviews);
  handleRefetchPreviewsRef.current = handleRefetchPreviews;

  useRegisterSaveAsNew((newTemplate, editedId) =>
    setTemplateForQuickSend((prev) => {
      const copy = makeDeepCopy(prev);
      const idx = copy.findIndex((t: Template) => t.id === editedId);
      if (idx >= 0) {
        copy[idx] = newTemplate;
        handleRefetchPreviewsRef.current(copy);
      }
      return copy;
    }),
  );

  const options =
    preview.contentType === TemplateContentType.OUTREACH
      ? outreachTemplates
      : followupTemplates;

  const fetchLastScheduledTemplate = async () => {
    if (!currentOrg || quickSendBrandId < 0) return;
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach/${quickSendBrandId}/last-scheduled-template`,
        "GET",
      );
      if (res.templateId) {
        const scheduledTemplate = templates?.find(
          (x) => x.id === res.templateId,
        );
        if (scheduledTemplate) {
          setScheduledTemplate(scheduledTemplate);
          setTemplateForQuickSend((prev) => {
            const copy = makeDeepCopy(prev);
            if (scheduledTemplate) copy[index] = scheduledTemplate;
            handleRefetchPreviews(copy);
            return copy;
          });
        }
      } else {
        setScheduledTemplate(null);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getTemplateValue = (t: Template) => {
    if (t.emailTemplateId) {
      return `${t.name}-${t.emailTemplateId}`;
    } else if (t.id) {
      return `${t.name}-${t.id}`;
    } else {
      return `${t.name}`;
    }
  };

  const handleChangeTemplate = (e: CustomEvent) => {
    const value = e.target.value;
    if (value === "Add Template +") {
      return;
    }

    trackEvent("Template Changed", {
      "Template Name": value,
    });

    let template: Template | null = null;
    for (const t of options) {
      const templateValue = getTemplateValue(t);
      if (value === templateValue) {
        template = t;
        break;
      }
    }

    if (!template) {
      return;
    }
    setTemplateForQuickSend((prev) => {
      const copy = makeDeepCopy(prev);
      if (template) copy[index] = template;
      handleRefetchPreviews(copy);
      return copy;
    });
  };

  const handleTrackAddTemplate = () => {
    trackEvent("Add Template Button Clicked", {
      "Clicked From": "Select Template Component",
    });
    setNewlyAddedTemplateIndex(index);
    setCreateTemplateDialogOpen(true);
  };

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "Quick Send",
    });
    setNewlyAddedTemplateIndex(index);
    setCreateTemplateDialogOpen(true);
    setCurrentTemplateContentType(preview?.contentType);
  };

  const hasTemplateSettings =
    emailSettings?.emailSettingTemplates &&
    emailSettings?.emailSettingTemplates?.length > 0;
  const presetTemplate = hasTemplateSettings
    ? emailSettings?.emailSettingTemplates?.find((x) => x.emailOrder === index)
    : null;

  const getSelectValue = () => {
    if (preview?.templateId) {
      return `${preview?.templateName}-${preview.templateId}`;
    } else {
      return preview?.templateName;
    }
  };

  const templateInList = () => {
    const exists = options?.find(
      (x) => getTemplateValue(x) === getSelectValue(),
    );
    return exists;
  };

  const isAIGenerated = (template: Template) => {
    return (
      template?.isAutogenerated &&
      template?.emailTemplateName === "Generated by Bento"
    );
  };

  const getTemplateName = (template: Template) => {
    if (isAIGenerated(template)) {
      return "Personalized For Brand";
    } else {
      return template?.name || template.subject;
    }
  };

  const renderTemplateOption = () => {
    if (previewLoading === quickSendBrandId) {
      return <Skeleton animation="wave" width={120} height={40} />;
    } else if (!templateInList() || options?.length <= 0) {
      return (
        <Button
          variant="contained"
          size="small"
          onClick={handleGenerateTemplate}
          sx={{ height: 25, textTransform: "none" }}
          disableElevation
        >
          Generate {isFollowUpEmail ? "Follow-Up" : "Template"}
        </Button>
      );
    } else {
      return (
        <>
          <Select
            onChange={(e: CustomEvent) => handleChangeTemplate(e)}
            value={getSelectValue()}
            size="small"
            sx={[
              styles.templateSelect,
              styles.noSelectBorder,
              styles.templateWidth,
            ]}
          >
            <MenuItem
              onClick={handleTrackAddTemplate}
              sx={{ color: "secondary.main" }}
            >
              <em>Add Template +</em>
            </MenuItem>
            {options?.map((template: Template, index: number) => (
              <MenuItem
                key={index}
                value={getTemplateValue(template)}
                sx={styles.templateMenuItem}
              >
                {isAIGenerated(template) && (
                  <Box
                    sx={{ color: "info.main", mr: 1 }}
                    component="i"
                    className="fa-solid fa-wand-magic-sparkles"
                  />
                )}
                <Box sx={styles.templateMenuName(isAIGenerated(template))}>
                  {getTemplateName(template)}
                </Box>

                {scheduledTemplate &&
                  template?.id === scheduledTemplate?.id && (
                    <Chip
                      size="small"
                      sx={styles.chip}
                      label="Scheduled Template"
                    />
                  )}
                {!scheduledTemplate &&
                  presetTemplate &&
                  template?.id === presetTemplate?.templateId && (
                    <Chip size="small" sx={styles.chip} label="Last Used" />
                  )}
              </MenuItem>
            ))}
          </Select>{" "}
          {preview?.templateId && templateInList()?.id && (
            <TemplateEditButton
              sx={{ ml: -2 }}
              templateId={preview?.templateId}
            />
          )}
        </>
      );
    }
  };

  useEffect(() => {
    fetchLastScheduledTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, quickSendBrandId]);

  return (
    <>
      {scheduledTemplate && (
        <Grid
          item
          xs={12}
          sx={{
            backgroundColor: "secondary.light",
            fontSize: 12,
            px: 2,
            py: 1,
          }}
        >
          You currently have a <strong>scheduled email</strong> with this brand.
        </Grid>
      )}
      <Grid container justifyContent="space-between" sx={{ pr: 1, mt: 1 }}>
        <Box sx={styles.subjectWrapper}>
          <Typography component="span" sx={styles.annotation}>
            Template:
          </Typography>
          {renderTemplateOption()}
          {showPersonalize && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                trackEvent("Personalize Button Clicked", {
                  "Brand ID": bentoBrand?.id,
                  Surface: "SelectTemplate",
                });
                setPersonalizeOpen(true);
              }}
              disableElevation
              aria-label="Personalize"
              sx={{
                ml: 1,
                height: 25,
                textTransform: "none",
                flexShrink: 0,
                minWidth: { xs: 30, sm: "auto" },
                px: { xs: 0.75, sm: 1.25 },
                background: "#8084FF",
                color: "white",
                "&:hover": { background: "#6970E8" },
              }}
            >
              <Box
                component="i"
                className="fa-solid fa-wand-magic-sparkles"
                sx={{ mr: { xs: 0, sm: 0.75 }, fontSize: 12 }}
              />
              <Box
                component="span"
                sx={{ display: { xs: "none", sm: "inline" } }}
              >
                Personalize
              </Box>
            </Button>
          )}
        </Box>

        {showPersonalize && bentoBrand && editor && (
          <PersonalizeDialog
            open={personalizeOpen}
            onClose={() => setPersonalizeOpen(false)}
            bentoBrand={bentoBrand}
            currentDraftHtml={editor.getHTML()}
            onApply={(html: string) => editor.commands.setContent(html)}
          />
        )}
      </Grid>
    </>
  );
}
